import { BLOCKS } from '@contentful/rich-text-types';
import { WindowLocation } from '@reach/router';
import { useIntl } from 'react-intl';
import React, { useMemo } from 'react';

import RichText from '../../UI/RichText';
import TextLink from '../../UI/TextLink/TextLink';
import { AnnouncementBannerData } from '../../../ContentfulTypes';

import {
  PreHeaderBar,
  ContentWrapper,
  LinkWrapper,
  CloseButton,
} from './styles';

interface Props {
  bannerData: AnnouncementBannerData;
  preferredProvince: string;
  location: WindowLocation<unknown>;
}

// RichText Options
const contentOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <p>{children}</p>,
    [BLOCKS.EMBEDDED_ENTRY]: (node, { locale } = useIntl()) => (
      <TextLink
        route={node.data.target?.ctaTarget}
        linkText={node.data.target?.ctaLabel?.ctaLabel}
        locale={locale}
        url={node.data.target?.ctaUrl}
      />
    ),
  },
};

const PreHeader = ({ bannerData, preferredProvince, location }: Props) => {
  const { locale } = useIntl();
  // Dismiss bar
  const closeBar = () => {
    document.getElementById('announcementBar')?.classList.add('hide');
  };

  // Banner colours
  const backgroundColours = {
    Success: '#BDE3B9',
    Warning: '#F0EBBC',
    Danger: '#ECBFBD',
    Info: '#B4D7E9',
  };

  // Object of pages possible for display, including trailing backslash versions
  const pagePaths = useMemo(() => ({
    [`/${locale}`]: 'Home',
    [`/${locale}/`]: 'Home',
    [`/${locale}/phones`]: 'Phones',
    [`/${locale}/phones/`]: 'Phones',
    [`/${locale}/promos`]: 'Promotions',
    [`/${locale}/promos/`]: 'Promotions',
    [`/${locale}/blog`]: 'Blog',
    [`/${locale}/blog/`]: 'Blog',
    [`/${locale}/locations`]: 'Locations',
    [`/${locale}/locations/`]: 'Locations',
    [`/${locale}/contact-us`]: 'Contact',
    [`/${locale}/contact-us/`]: 'Contact',
    [`/${locale}/accessories`]: 'Accessories',
    [`/${locale}/accessories/`]: 'Accessories',
  }), [locale]);

  // Should banner display in this province and on this page?
  const displayBanner = useMemo(() => {
    if (
      bannerData.bannerDisplayProvinces.includes(preferredProvince)
      && bannerData.bannerDisplayPages.includes(pagePaths[location.pathname])
    ) {
      return true;
    }
    return false;
  }, [
    bannerData.bannerDisplayPages,
    bannerData.bannerDisplayProvinces,
    location.pathname,
    pagePaths,
    preferredProvince,
  ]);

  if (!displayBanner) return null;

  return (
    <PreHeaderBar
      id="announcementBar"
      colour={backgroundColours[bannerData.bannerColor]}
      data-testid="announcementBar"
    >
      <ContentWrapper>
        <LinkWrapper>
          <RichText
            document={bannerData.bannerContent}
            options={contentOptions}
          />
        </LinkWrapper>
        {bannerData.isDismissible && (
          <CloseButton onClick={closeBar}>x</CloseButton>
        )}
      </ContentWrapper>
    </PreHeaderBar>
  );
};

export default PreHeader;
